import { useCallback, useEffect, useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { flushSync } from 'react-dom'
import { useLocation, useNavigate, useParams } from 'react-router'
import { NotFoundPage } from '@/app/ErrorPage'
import { ProductDeleteDialog } from '@/features/product-catalog/components/ProductDeleteDialog'
import { ProductSourcesPanel } from '@/features/product-catalog/components/ProductSourcesPanel'
import { ProductSourceDetail } from '@/features/product-catalog/pages/ProductSourceDetail'
import { useInFlightProductSourceUploadIds } from '@/features/product-catalog/hooks/useInFlightProductSourceUploadIds'
import { useProductFileSourceUpload } from '@/features/product-catalog/hooks/useProductFileSourceUpload'
import {
  useListProductComponents,
  type ProductBomImportResult,
} from '@/shared/hooks/api/catalog'
import {
  PRODUCT_LAW_KEYS,
  useDiscoverV2Product,
  useGetProduct,
  useGetProductApplicability,
  useInvalidateOnDiscoverWorkflowTerminal,
  useProductDiscoverWorkflowLatest,
} from '@/shared/hooks/api/product'
import { useListProductSources } from '@/shared/hooks/api/productSource'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { V2ProductDiscoverScopeChamber } from '../components/V2ProductDiscoverScopeChamber'
import { V2ProductCatalogSupplierCard } from '../components/V2ProductCatalogSupplierCard'
import { V2ProductDetailsCard } from '../components/V2ProductDetailsCard'
import { V2ProductManufacturerInformationCard } from '../components/V2ProductManufacturerInformationCard'
import { V2ProductPartsSummary } from '../components/V2ProductPartsSummary'
import { V2ProductGetStartedCard } from '../components/V2ProductGetStartedCard'
import { V2ProductRegulationsSummaryCard } from '../components/V2ProductRegulationsSummaryCard'
import { V2ProductPartsPage } from './V2ProductPartsPage'
import { V2ProductEmcRegulationPage } from './product-regulations/V2ProductEmcRegulationPage'
import { V2ProductFgasRegulationPage } from './product-regulations/V2ProductFgasRegulationPage'
import { V2ProductPedRegulationPage } from './product-regulations/V2ProductPedRegulationPage'
import { V2ProductLvdRegulationPage } from './product-regulations/V2ProductLvdRegulationPage'
import { V2ProductMachineryRegulationPage } from './product-regulations/V2ProductMachineryRegulationPage'
import { V2ProductRedRegulationPage } from './product-regulations/V2ProductRedRegulationPage'
import { V2ProductRohsRegulationPage } from './product-regulations/V2ProductRohsRegulationPage'
import { V2ProductWeeeRegulationPage } from './product-regulations/V2ProductWeeeRegulationPage'
import {
  getV2ProductDetailPath,
  getV2ProductLawFromPath,
  getV2ProductPartsPath,
  getV2ProductSourceInspectSourceId,
  isUnknownV2ProductNestedPath,
  isV2ProductPartsPath,
  V2_PRODUCTS_PATH,
} from '../utils/routes'
import { isProductDiscoverWorkflowRunning } from '../utils/productDiscoverWorkflow'

const PRODUCT_CATALOG_NAMESPACES = ['productCatalog'] as const

type ProductPartsLocationState = {
  importFailures?: ProductBomImportResult['failed']
}

type V2ProductDetailContentProps = {
  productId: string
  onImportFailures: (failures: ProductBomImportResult['failed']) => void
  showDiscoverChamber?: boolean
}

function V2ProductDetailContent({
  productId,
  onImportFailures,
  showDiscoverChamber = true,
}: V2ProductDetailContentProps) {
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'productCatalog'])
  const { data: product, isLoading: isLoadingProduct } = useGetProduct(productId)
  const { data: applicability, isLoading: isLoadingApplicability } =
    useGetProductApplicability(productId)
  const discoverMutation = useDiscoverV2Product(productId)
  const { data: discoverWorkflow } = useProductDiscoverWorkflowLatest(productId)
  useInvalidateOnDiscoverWorkflowTerminal(productId, discoverWorkflow)
  const { data: sources = [] } = useListProductSources(productId)
  const { handleFileSelect, handleMultiFileSelect, uploadingSourceIds } =
    useProductFileSourceUpload(productId)
  const inFlightUploadSourceIds = useInFlightProductSourceUploadIds(productId)
  const activeUploadSourceIds = useMemo(() => {
    const merged = new Set(uploadingSourceIds)
    for (const sourceId of inFlightUploadSourceIds) {
      merged.add(sourceId)
    }
    return merged
  }, [uploadingSourceIds, inFlightUploadSourceIds])
  const { data: productComponents = [], isLoading: isLoadingComponents } =
    useListProductComponents(productId)
  const [discoverSheetOpen, setDiscoverSheetOpen] = useState(false)
  const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false)

  const title =
    product?.name?.trim() || t('v2.pages.products.fallbackTitle')
  const skuLabel =
    product?.sku != null && product.sku.trim() !== ''
      ? product.sku.trim()
      : null
  const isLoadingApplicabilitySection =
    isLoadingProduct || isLoadingApplicability

  const anyLawRunning = PRODUCT_LAW_KEYS.some((law) => {
    return applicability?.[law]?.runState === 'running'
  })
  const discoverWorkflowRunning = isProductDiscoverWorkflowRunning(discoverWorkflow)

  useEffect(() => {
    if (discoverWorkflowRunning) {
      setDiscoverSheetOpen(true)
    }
  }, [discoverWorkflowRunning])

  const handleDiscover = useCallback(() => {
    setDiscoverSheetOpen(true)
    if (
      discoverMutation.isPending ||
      discoverWorkflowRunning ||
      anyLawRunning
    ) {
      return
    }
    discoverMutation.mutate()
  }, [anyLawRunning, discoverMutation, discoverWorkflowRunning])

  const isDiscoverRunning =
    discoverMutation.isPending ||
    discoverWorkflowRunning ||
    anyLawRunning
  const isDiscoverDisabled = discoverMutation.isPending

  return (
    <div className="space-y-6">
      <header className="space-y-3">
        {isLoadingProduct ? (
          <>
            <Skeleton className="h-8 w-64 max-w-full" />
            <Skeleton className="h-4 w-32" />
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
                {skuLabel != null && (
                  <p className="text-muted-foreground">
                    {t('v2.pages.products.detail.skuLabel', { sku: skuLabel })}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={isDiscoverDisabled}
                  onClick={handleDiscover}
                >
                  {t('v2.pages.products.detail.discoverButton')}
                </Button>
                {product != null && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    aria-label={t('productCatalog:detail.deleteProductAriaLabel', {
                      title,
                    })}
                    onClick={() => setDeleteProductDialogOpen(true)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                    {t('actions.delete')}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </header>

      {product != null && (
        <ProductDeleteDialog
          open={deleteProductDialogOpen}
          onOpenChange={setDeleteProductDialogOpen}
          product={product}
          linkedChatCount={0}
          onDeleted={() => navigate(V2_PRODUCTS_PATH)}
        />
      )}

      {showDiscoverChamber && (
        <V2ProductDiscoverScopeChamber
          productId={productId}
          workflow={discoverWorkflow}
          open={discoverSheetOpen}
          onOpenChange={setDiscoverSheetOpen}
        />
      )}

      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-1">
          <ProductSourcesPanel
            productId={productId}
            sources={sources}
            uploadingSourceIds={activeUploadSourceIds}
            onUploadFile={handleFileSelect}
            onMultiFileUpload={handleMultiFileSelect}
            productDetailPath={getV2ProductDetailPath(productId)}
          />

          <V2ProductDetailsCard productId={productId} />

          <V2ProductManufacturerInformationCard productId={productId} />

          <V2ProductCatalogSupplierCard productId={productId} />

          <V2ProductPartsSummary
            productId={productId}
            productComponents={productComponents}
            isLoading={isLoadingComponents}
            onImportFailures={onImportFailures}
          />
        </div>

        <div className="space-y-6 lg:col-span-2">
          <V2ProductGetStartedCard productId={productId} />
          <V2ProductRegulationsSummaryCard
            productId={productId}
            applicability={applicability}
            isLoading={isLoadingApplicabilitySection}
            isDiscoverRunning={isDiscoverRunning}
            isDiscoverDisabled={isDiscoverDisabled}
            onDiscover={handleDiscover}
          />
        </div>
      </div>
    </div>
  )
}

export default function V2ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const {
    isLoading: isLoadingProduct,
    isError: isProductError,
    error: productError,
  } = useGetProduct(productId ?? '')
  const sourceId =
    productId != null
      ? getV2ProductSourceInspectSourceId(location.pathname, productId)
      : undefined
  const showPartsPage =
    productId != null && isV2ProductPartsPath(location.pathname, productId)
  const regulationLaw =
    productId != null
      ? getV2ProductLawFromPath(location.pathname, productId)
      : undefined
  const showUnknownNestedPath =
    productId != null &&
    isUnknownV2ProductNestedPath(location.pathname, productId)
  const partsLocationState = location.state as ProductPartsLocationState | null
  const [forceProductView, setForceProductView] = useState(false)
  const [retainedInspectSourceId, setRetainedInspectSourceId] = useState<
    string | undefined
  >()
  const showSourceDetail = sourceId != null && !forceProductView
  const inspectSourceId = sourceId ?? retainedInspectSourceId
  const showProductOverview =
    !showSourceDetail && !showPartsPage && regulationLaw == null

  useEffect(() => {
    void i18n.loadNamespaces([...PRODUCT_CATALOG_NAMESPACES])
  }, [])

  useEffect(() => {
    if (sourceId != null) {
      setRetainedInspectSourceId(sourceId)
    }
  }, [sourceId])

  useEffect(() => {
    if (sourceId == null) {
      setForceProductView(false)
    }
  }, [sourceId])

  useEffect(() => {
    if (sourceId != null || retainedInspectSourceId == null) {
      return
    }
    const frameId = window.requestAnimationFrame(() => {
      setRetainedInspectSourceId(undefined)
    })
    return () => window.cancelAnimationFrame(frameId)
  }, [sourceId, retainedInspectSourceId])

  const handleBackToProduct = useCallback(() => {
    if (productId == null) {
      return
    }
    flushSync(() => {
      setForceProductView(true)
    })
    navigate(getV2ProductDetailPath(productId))
  }, [navigate, productId])

  const handleImportFailures = useCallback(
    (failures: ProductBomImportResult['failed']) => {
      if (productId == null) {
        return
      }
      navigate(getV2ProductPartsPath(productId), {
        state: { importFailures: failures },
      })
    },
    [navigate, productId]
  )

  if (productId == null || productId.length === 0) {
    return null
  }

  if (!isLoadingProduct && isProductError) {
    if (isAxiosError(productError) && productError.response?.status === 404) {
      return <NotFoundPage />
    }
  }

  if (showUnknownNestedPath) {
    return <NotFoundPage />
  }

  return (
    <>
      <div hidden={!showProductOverview}>
        <V2ProductDetailContent
          productId={productId}
          onImportFailures={handleImportFailures}
          showDiscoverChamber={showProductOverview}
        />
      </div>
      {showPartsPage && (
        <V2ProductPartsPage
          productId={productId}
          initialImportFailures={partsLocationState?.importFailures ?? []}
        />
      )}
      {regulationLaw === 'machinery' && (
        <V2ProductMachineryRegulationPage productId={productId} />
      )}
      {regulationLaw === 'rohs' && (
        <V2ProductRohsRegulationPage productId={productId} />
      )}
      {regulationLaw === 'emc' && (
        <V2ProductEmcRegulationPage productId={productId} />
      )}
      {regulationLaw === 'lvd' && (
        <V2ProductLvdRegulationPage productId={productId} />
      )}
      {regulationLaw === 'red' && (
        <V2ProductRedRegulationPage productId={productId} />
      )}
      {regulationLaw === 'weee' && (
        <V2ProductWeeeRegulationPage productId={productId} />
      )}
      {regulationLaw === 'fgas' && (
        <V2ProductFgasRegulationPage productId={productId} />
      )}
      {regulationLaw === 'ped' && (
        <V2ProductPedRegulationPage productId={productId} />
      )}
      {inspectSourceId != null && (
        <div hidden={!showSourceDetail}>
          <ProductSourceDetail
            sourceId={inspectSourceId}
            active={showSourceDetail}
            onBackToProduct={handleBackToProduct}
          />
        </div>
      )}
    </>
  )
}
